import * as THREE from "three";
import { Vector2 } from "three";
import AppData from "../../../app.data";
import CameraService from "../../camera/camera.service";
import Renderer from "../../../renderer/renderer";
import { EventRaycaster } from "./raycast.handler";
import { EventMediaHandler } from "./media.handler";
import { EventArtworkManipulator } from "./artwork.handler";
import { resolveRenderQualityCapsForQuality } from "../../../renderer/qualityCaps";

const CAMERA_JOYSTICK_SPEED = 0.1;
const ENABLE_VIEWER_ROTATION_ENV = "REACT_APP_ENABLE_3D_ARTWORK_DRAG_ROTATION";
const TOUCH_TAP_MAX_DURATION_MS = 500;
const TOUCH_DRAG_THRESHOLD_PX = 12;
const DOUBLE_TAP_MAX_DELAY_MS = 350;
const DOUBLE_TAP_MAX_DISTANCE_PX = 24;
const TOUCH_COMPATIBILITY_MOUSE_WINDOW_MS = 800;

export class EventInputHandler {
    private appData: AppData;
    private cameraService: CameraService;
    private renderService: Renderer;
    private raycasterHandler: EventRaycaster;
    private mediaHandler: EventMediaHandler;
    private artworkHandler: EventArtworkManipulator;
    private canvas: HTMLCanvasElement;

    public isDrag = false;
    public started = false;
    public startX = 0;
    public startY = 0;
    public twoFingersDistance = 0;
    public map = {} as any;
    private keyUpAvailability: boolean = true;

    // Joystick props
    private joystickThumb = document.getElementById("joystick-thumb");
    private previousStyleLeft: any;
    private deltax = new Vector2(0, 0)
    private loopWhileJoystickPressed: any;
    private speed_value = 0.0005;
    private startTime = Date.now();

    // Touch props
    private touchTimeStart = 0;
    private touchesMap = {} as any;
    private touchStartedWithSinglePointer = false;
    private touchDragged = false;
    private lastMobileTapAt: number | null = null;
    private lastMobileTapPosition: Vector2 | null = null;
    private lastTouchInteractionAt: number | null = null;
    private viewerRotationEnabled = false;
    private viewerRotationCandidate: THREE.Object3D | null = null;
    private viewerRotationTarget: THREE.Object3D | null = null;
    private viewerRotationOriginal: THREE.Euler | null = null;
    private viewerRotationDragging = false;
    private viewerRotationLastPointer = new Vector2(0, 0);

    constructor(
        cameraService: CameraService,
        renderService: Renderer,
        raycasterHandler: EventRaycaster,
        mediaHandler: EventMediaHandler,
        artworkHandler: EventArtworkManipulator,
        canvas: HTMLCanvasElement
    ) {
        this.cameraService = cameraService;
        this.renderService = renderService;
        this.raycasterHandler = raycasterHandler;
        this.mediaHandler = mediaHandler;
        this.artworkHandler = artworkHandler;
        this.canvas = canvas;
        this.appData = new AppData();
        this.appData.restoreViewerRotation = this.restoreViewerRotation;
    }

    public setKeyUpAvailability(availability: boolean) {
        this.keyUpAvailability = availability;
        if (!availability) {
            this.appData.keyStrokes = {};
        }
    }

    public setViewerRotationEnabled(enabled: boolean) {
        this.viewerRotationEnabled = enabled;
        if (!enabled) {
            this.restoreViewerRotation();
        }
    }

    public restoreViewerRotation = () => {
        if (this.viewerRotationTarget && this.viewerRotationOriginal) {
            this.viewerRotationTarget.rotation.copy(this.viewerRotationOriginal);
            this.renderService.addToRenderQueue();
        }
        this.viewerRotationCandidate = null;
        this.viewerRotationTarget = null;
        this.viewerRotationOriginal = null;
        this.viewerRotationDragging = false;
        this.appData.viewerRotationActive = false;
    }

    public keyDownEvent = (event) => {
        if (!this.keyUpAvailability || this.isEditableTarget(event.target)) {
            return;
        }

        this.appData.keyStrokes[event.keyCode] = event.key;

        const selectedArtwork = this.artworkHandler.getSelectedArtwork();

        if (this.artworkHandler.transformControls && selectedArtwork) {
            switch (event.key.toLowerCase()) {
                case 'z':
                    this.artworkHandler.transformControls.setMode('translate');
                    this.renderService.addToRenderQueue();
                    break;
                case 'x':
                    this.artworkHandler.transformControls.setMode('rotate');
                    this.renderService.addToRenderQueue();
                    break;
            }
        }
    };

    public keyUpEvent = (event) => {
        delete this.appData.keyStrokes[event.keyCode]
    };

    private isEditableTarget(target: EventTarget | null | undefined): boolean {
        if (!(target instanceof Element)) {
            return false;
        }

        return Boolean(target.closest("input, textarea, select, [contenteditable]:not([contenteditable='false'])"));
    }

    public joystickTouchStart(event: TouchEvent) {
        this.joystickThumb = document.getElementById("joystick-thumb");
        this.previousStyleLeft = this.joystickThumb?.getBoundingClientRect();
        event.preventDefault();


        this.startTime = Date.now();

        this.loopWhileJoystickPressed = setInterval(() => {
            this.cameraService.controlMoveForward(-(this.deltax.y / 3) * CAMERA_JOYSTICK_SPEED);
            this.cameraService.controlMoveRight((this.deltax.x / 3) * CAMERA_JOYSTICK_SPEED * 0.4);
            this.renderService.addToRenderQueue()
        }, 50); // Reduced from 30ms to 50ms for better VR performance
    }

    public joystickTouchMove(event) {
        event.preventDefault();
        let centerPoint = new Vector2(this.previousStyleLeft.x, this.previousStyleLeft.y);
        let currEv = event.targetTouches[0];

        let currentPoint1 = new Vector2(currEv.clientX - this.previousStyleLeft.width / 2, currEv.clientY - this.previousStyleLeft.height / 2);
        let distance = centerPoint.distanceTo(currentPoint1);
        this.deltax = currentPoint1.clone().sub(centerPoint.clone());

        let x1 = currentPoint1.x;
        let y1 = currentPoint1.y;

        let radius = this.previousStyleLeft.width;
        if (distance >= radius) {
            x1 = x1 - centerPoint.x;
            y1 = y1 - centerPoint.y;
            var radians = Math.atan2(y1, x1)
            x1 = Math.cos(radians) * radius + centerPoint.x;
            y1 = Math.sin(radians) * radius + centerPoint.y;
            this.deltax = new Vector2(x1, y1).clone().sub(centerPoint)
        }

        if (this.joystickThumb) {
            this.joystickThumb.style.left = x1 + "px";
            this.joystickThumb.style.top = y1 + "px";
        }

    }

    public joystickTouchEnd(event) {
        event.preventDefault();
        clearInterval(this.loopWhileJoystickPressed)
        this.joystickThumb = document.getElementById("joystick-thumb");

        if (this.joystickThumb) {
            this.joystickThumb.style.left = "65px"
            this.joystickThumb.style.top = (window.innerHeight - 55 - 70) + "px"
        }
        this.deltax.x = 0
        this.deltax.y = 0

    }

    public touchStart = (event: TouchEvent) => {
        event.preventDefault();
        this.touchTimeStart = Date.now();
        this.lastTouchInteractionAt = this.touchTimeStart;
        this.started = true;
        this.touchStartedWithSinglePointer = event.touches.length === 1;
        this.touchDragged = false;
        if (!this.touchStartedWithSinglePointer) {
            this.clearMobileTapCandidate();
        }
        const pointer = this.getPointerPosition(event);
        if (pointer) {
            this.startX = pointer.x;
            this.startY = pointer.y;
            this.prepareViewerRotation(event, pointer.x, pointer.y);
        }
        // Initialize or clear the touches map.
        this.touchesMap = {};
        for (let i = 0; i < event.touches.length; i++) {
            const touch = event.touches[i];
            this.touchesMap[touch.identifier] = { startX: touch.pageX, startY: touch.pageY };
        }
        this.renderService.addToRenderQueue()
    };

    public touchMove = (event: TouchEvent) => {
        event.preventDefault();
        const pointer = this.getPointerPosition(event);
        if (
            event.touches.length !== 1 ||
            (pointer && Math.hypot(pointer.x - this.startX, pointer.y - this.startY) > TOUCH_DRAG_THRESHOLD_PX)
        ) {
            this.touchDragged = true;
            this.clearMobileTapCandidate();
        }
        if (pointer && this.handleViewerRotationMove(pointer.x, pointer.y)) {
            return;
        }

        let totalDiffX = 0;
        let totalDiffY = 0;
        const touchCount = event.touches.length;

        // Process each active touch.
        for (let i = 0; i < touchCount; i++) {
            const touch = event.touches[i];
            // Retrieve the stored starting position for this touch.
            const previous = this.touchesMap[touch.identifier];
            if (previous) {
                const diffX = touch.pageX - previous.startX;
                const diffY = touch.pageY - previous.startY;
                totalDiffX += diffX;
                totalDiffY += diffY;
                // Update the stored position with the current one.
                this.touchesMap[touch.identifier] = { startX: touch.pageX, startY: touch.pageY };
            } else {
                // If somehow the touch wasn't stored, initialize it.
                this.touchesMap[touch.identifier] = { startX: touch.pageX, startY: touch.pageY };
            }
        }

        // Use the average difference across all touches to control the camera.
        if (touchCount > 0) {
            const avgDiffX = totalDiffX / touchCount;
            const avgDiffY = totalDiffY / touchCount;

            this.cameraService.controlRotateCamera(avgDiffX, avgDiffY);
            this.renderService.addToRenderQueue()
        }
    };

    public touchEnd = (event: TouchEvent) => {
        event.preventDefault();
        const touchTimeEnd = Date.now();
        this.lastTouchInteractionAt = touchTimeEnd;
        const pointer = this.getPointerPosition(event);
        this.started = false;
        this.map = [];
        const rotatedArtwork = this.finishViewerRotation();
        const isTap = (
            !rotatedArtwork &&
            this.touchStartedWithSinglePointer &&
            !this.touchDragged &&
            event.touches.length === 0 &&
            pointer !== null &&
            touchTimeEnd - this.touchTimeStart <= TOUCH_TAP_MAX_DURATION_MS
        );
        if (isTap && pointer) {
            const isDoubleTap = (
                this.lastMobileTapAt !== null &&
                this.lastMobileTapPosition !== null &&
                touchTimeEnd - this.lastMobileTapAt <= DOUBLE_TAP_MAX_DELAY_MS &&
                this.lastMobileTapPosition.distanceTo(new Vector2(pointer.x, pointer.y)) <= DOUBLE_TAP_MAX_DISTANCE_PX
            );
            if (isDoubleTap) {
                this.raycasterHandler.slotClickHandler(event, "mobile-double-tap");
                this.clearMobileTapCandidate();
            } else {
                this.raycasterHandler.slotClickHandler(event, "mobile-single-tap");
                this.lastMobileTapAt = touchTimeEnd;
                this.lastMobileTapPosition = new Vector2(pointer.x, pointer.y);
            }
        } else {
            this.clearMobileTapCandidate();
        }
        this.touchStartedWithSinglePointer = false;
        this.touchDragged = false;
        setTimeout(() => {
            this.cameraService.lastMouse = null;
        }, 50);
        this.renderService.addToRenderQueue()
    };

    public mouseDown = (event) => {
        if (this.isTouchCompatibilityMouseEvent()) {
            this.started = false;
            return;
        }
        this.startX = event.clientX;
        this.startY = event.clientY;
        this.started = true;
        this.isDrag = false;
        this.prepareViewerRotation(event, event.clientX, event.clientY);
    };

    public mouseMove = (event) => {
        if (this.isTouchCompatibilityMouseEvent()) return;
        // Check if we're in VR mode and update cursor
        if (this.renderService.renderer && this.renderService.renderer.xr.isPresenting) {
            this.updateVRCursorFromEvent(event);
            return; // Skip camera rotation in VR mode
        }

        const dragThreshlod = 3
        if (this.started === true && event.buttons > 0) {
            if (this.handleViewerRotationMove(event.clientX, event.clientY)) {
                this.isDrag = true;
                return;
            }
            const diffX = Math.abs(event.clientX - this.startX);
            const diffY = Math.abs(event.clientY - this.startY);
            if (diffX < dragThreshlod && diffY < dragThreshlod) {
                this.isDrag = false;
            } else {
                this.isDrag = true;
                this.cameraService.controlRotateCamera(event.movementX, event.movementY);
                this.cameraService.saveCameraPositions()
                this.renderService.addToRenderQueue()
            }
        }
    };

    public passiveMouseMove = (event) => {
        // Only update VR cursor in VR mode
        if (this.renderService.renderer && this.renderService.renderer.xr.isPresenting) {
            this.updateVRCursorFromEvent(event);
        }
    };

    private updateVRCursorFromEvent(event: MouseEvent) {
        if (!this.renderService.updateVRCursor) return;

        // Convert mouse coordinates to normalized device coordinates
        const rect = this.renderService.renderer.domElement.getBoundingClientRect(); // Use renderer dom element
        const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update VR cursor position
        this.renderService.updateVRCursor(mouseX, mouseY);
    }

    public mouseUp = (event) => {
        this.started = false
        if (this.isTouchCompatibilityMouseEvent()) {
            this.mediaHandler.playAllVideos();
            return;
        }
        const rotatedArtwork = this.finishViewerRotation();
        if (!rotatedArtwork && !this.isDrag) {
            this.raycasterHandler.slotClickHandler(event)
        }

        this.mediaHandler.playAllVideos();
    };

    public mouseLeave = (event) => {
        this.started = false
        this.finishViewerRotation();
    };

    private canUseViewerRotation() {
        return (
            this.viewerRotationEnabled &&
            process.env[ENABLE_VIEWER_ROTATION_ENV] === "true" &&
            this.appData.galleryData?.data?.pseudoMomManualRotateObjects === true &&
            !this.appData.galleryData?.editable &&
            !this.renderService.renderer?.xr?.isPresenting &&
            !this.artworkHandler.isArtowrkSelectToggle
        );
    }

    private prepareViewerRotation(event: MouseEvent | TouchEvent, x: number, y: number) {
        if (!this.canUseViewerRotation()) return;

        const target = this.raycasterHandler.getRotatableArtworkFromEvent(event);
        if (!target) {
            this.restoreViewerRotation();
            return;
        }

        if (this.viewerRotationTarget && this.viewerRotationTarget !== target) {
            this.restoreViewerRotation();
        }

        this.viewerRotationCandidate = target;
        this.viewerRotationLastPointer.set(x, y);
    }

    private handleViewerRotationMove(x: number, y: number) {
        if (!this.viewerRotationCandidate || !this.canUseViewerRotation()) return false;

        const diffX = x - this.viewerRotationLastPointer.x;
        const diffY = y - this.viewerRotationLastPointer.y;
        if (!this.viewerRotationDragging) {
            const startDiffX = Math.abs(x - this.startX);
            const startDiffY = Math.abs(y - this.startY);
            if (startDiffX < 3 && startDiffY < 3) {
                return false;
            }
            this.viewerRotationDragging = true;
            this.viewerRotationTarget = this.viewerRotationCandidate;
            this.viewerRotationOriginal = this.viewerRotationTarget.rotation.clone();
            this.appData.viewerRotationActive = true;
        }

        if (this.viewerRotationTarget) {
            this.viewerRotationTarget.rotation.y += diffX * 0.01;
            this.viewerRotationTarget.rotation.x += diffY * 0.01;
            this.renderService.addToRenderQueue();
        }
        this.viewerRotationLastPointer.set(x, y);
        return true;
    }

    private finishViewerRotation() {
        const rotatedArtwork = this.viewerRotationDragging;
        if (rotatedArtwork) {
            this.restoreViewerRotation();
        } else {
            this.viewerRotationCandidate = null;
            this.viewerRotationDragging = false;
            this.appData.viewerRotationActive = false;
        }
        return rotatedArtwork;
    }

    private getPointerPosition(event: MouseEvent | TouchEvent) {
        const touchEvent = event as TouchEvent;
        const touch = touchEvent.touches?.[0] || touchEvent.changedTouches?.[0];
        if (touch) return { x: touch.clientX, y: touch.clientY };
        const mouseEvent = event as MouseEvent;
        if (typeof mouseEvent.clientX === "number" && typeof mouseEvent.clientY === "number") {
            return { x: mouseEvent.clientX, y: mouseEvent.clientY };
        }
        return null;
    }

    private clearMobileTapCandidate() {
        this.lastMobileTapAt = null;
        this.lastMobileTapPosition = null;
    }

    private isTouchCompatibilityMouseEvent() {
        return (
            this.lastTouchInteractionAt !== null &&
            Date.now() - this.lastTouchInteractionAt <= TOUCH_COMPATIBILITY_MOUSE_WINDOW_MS
        );
    }

    public onWindowResize() {
        let canvasRect = this.canvas.getBoundingClientRect();
        let width = canvasRect.width;
        let height = canvasRect.height;

        // keep renderer service's canvasRect in sync
        if (this.appData.renderService) {
            this.appData.renderService.canvasRect = canvasRect as any;
        }

        if (this.appData.camera) {
            this.appData.camera.aspect = width / height;
            this.appData.camera.updateProjectionMatrix();
        }
        if (this.appData.debugCamera) {
            this.appData.debugCamera.aspect = width / height;
            this.appData.debugCamera.updateProjectionMatrix();
        }

        if (this.appData.renderService && this.appData.renderService.renderer) {
            this.appData.renderService.renderer.setSize(width, height);
            // Adjust composer resolution based on render quality
            const renderService = this.appData.renderService as any;
            if (renderService && renderService.composer) {
                const scale = (renderService.getRenderQualityCaps?.() || resolveRenderQualityCapsForQuality(renderService.renderQuality)).postProcessingScale;
                const w = Math.max(1, Math.floor(width * scale));
                const h = Math.max(1, Math.floor(height * scale));
                renderService.composer.setSize(w, h);
                const fxaaPass = (renderService.composer.passes || []).find((p: any) => p && p.name === "fxaa");
                if (fxaaPass && fxaaPass.uniforms && fxaaPass.uniforms["resolution"]) {
                    fxaaPass.uniforms["resolution"].value.x = 1 / w;
                    fxaaPass.uniforms["resolution"].value.y = 1 / h;
                }
            }
            this.appData.renderService.addToRenderQueue()
        }

        if (this.joystickThumb) {
            this.joystickThumb.style.left = "65px"
            this.joystickThumb.style.top = (window.innerHeight - 55 - 70) + "px"
        }
    }
}
